<script setup lang="ts">
import type { BlogPost } from "~/types/blog";

type PostSummary = Omit<BlogPost, "content">;

definePageMeta({
  layout: "default",
});

const config = useRuntimeConfig();
const siteOrigin = config.public.mainUrl as string;

// `useFetch`, not `$fetch`: this has to render server-side or the posts are
// invisible to crawlers, which is the entire point of publishing them here.
const { data, error } = await useFetch<{ posts: PostSummary[] }>("/api/blog");

const posts = computed(() => data.value?.posts ?? []);

// The template distinguishes an upstream failure from an empty feed on purpose.
// The feed is a third-party dependency: "nothing published yet" is a different
// message from "we couldn't load this", and telling a visitor the blog is empty
// when Soro is down is simply wrong. (Kept here rather than as a template
// comment — Vue serialises template comments into the SSR HTML in dev.)

useSeoMeta({
  title: "Blog | &Dine",
  description:
    "Ideas, guides and stories on office catering in London — from team lunches and dietary planning to working with independent Makers.",
  ogType: "website",
  ogUrl: `${siteOrigin}/blog`,
  ogTitle: "Blog | &Dine",
  ogDescription: "Ideas, guides and stories on office catering in London.",
});

useHead({
  link: [{ rel: "canonical", href: `${siteOrigin}/blog` }],
});

// One connected graph rather than separate islands — see `useSchemaGraph`.
useSchemaGraph("ld-blog", () => [
  {
    "@type": "Blog",
    "@id": `${siteOrigin}/blog#blog`,
    name: "&Dine Blog",
    url: `${siteOrigin}/blog`,
    description: "Ideas, guides and stories on office catering in London.",
    publisher: { "@id": `${siteOrigin}/#organization` },
    blogPost: posts.value.map((post) => ({
      "@type": "BlogPosting",
      "@id": `${siteOrigin}/blog/${post.slug}#article`,
      headline: post.title,
      url: `${siteOrigin}/blog/${post.slug}`,
      ...(post.publishedAt ? { datePublished: post.publishedAt } : {}),
    })),
  },
  {
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: siteOrigin },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${siteOrigin}/blog` },
    ],
  },
]);
</script>

<template>
  <section class="blog">
    <header class="blog__header">
      <h1 class="blog__title">
        The &amp;Dine blog
      </h1>
      <p class="blog__intro">
        Ideas, guides and stories on feeding a team well — from the people building
        London's office catering marketplace.
      </p>
    </header>

    <div class="blog__content">
      <div
        v-if="error"
        class="blog__state"
      >
        <fa :icon="['fal', 'exclamation-circle']" />
        <h2>We couldn't load the blog</h2>
        <p>Something went wrong fetching our latest posts. Please try again shortly.</p>
      </div>

      <div
        v-else-if="!posts.length"
        class="blog__state"
      >
        <fa :icon="['fal', 'book']" />
        <h2>No posts yet</h2>
        <p>We're writing our first articles now — check back soon.</p>
      </div>

      <div
        v-else
        class="blog__grid"
      >
        <BlogPostCard
          v-for="post in posts"
          :key="post.slug"
          :post="post"
        />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.blog {
  padding-top: 5rem; // clears the fixed NavBar

  &__header {
    max-width: 1160px;
    margin: 0 auto;
    padding: var(--space-6) 5% var(--space-4);
    text-align: center;
  }

  &__title {
    font-size: var(--text-4xl);
    font-weight: var(--weight-semibold);

    @media (max-width: $mobile) {
      font-size: var(--text-3xl);
    }
  }

  &__intro {
    max-width: 38rem;
    margin: var(--space-3) auto 0;
    color: var(--color-grey-2);
    line-height: 1.6;
  }

  &__content {
    max-width: 1160px;
    margin: 0 auto;
    padding: var(--space-4) 5% var(--space-8);
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: var(--space-4);

    @media (min-width: $mobile) {
      grid-template-columns: repeat(2, 1fr);
    }

    @media (min-width: $laptop) {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-7) var(--space-3);
    color: var(--color-grey-2);
    text-align: center;

    svg {
      margin-bottom: var(--space-2);
      font-size: var(--text-3xl);
    }

    h2 {
      color: var(--color-grey-1);
      font-size: var(--text-xl);
      font-weight: var(--weight-semibold);
    }
  }
}
</style>
